import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Popover,
  InputAdornment,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import StartVideoCallDialog from "../../../components/MessageHistory/StartVideoCallDialog";
import { useUsers } from "../../../hooks/useUsers";
import { useAgents } from "../../../hooks/useAgents";
import FilePreview from "../../../components/MessageHistory/FilePreview";
import AudioPreview from "../../../components/MessageHistory/AudioPreview";
import RecordingIndicator from "../../../components/MessageHistory/RecordingIndicator";
import EmojiPicker from "../../../components/MessageHistory/EmojiPicker";
import InternalNoteExpandedEditor from "../../../components/MessageHistory/InternalNoteExpandedEditor";
import ComposeAttachMenu from "../../../components/MessageHistory/ComposeAttachMenu";
import { composerHasSendPayload } from "../../../components/MessageHistory/composeAttachmentHelpers";
import {
  composerHasTextPayload,
  composerPlainText,
  isProbablyHtml,
  stripHtmlToPlain,
} from "../../../components/MessageHistory/composerHtmlUtils";
import { useComposerAudioRecording } from "../../../components/MessageHistory/useComposerAudioRecording";
import {
  COMPOSER_ROW_HEIGHT_PX,
  composerPrimaryActionButtonSx,
} from "../../../components/MessageHistory/composerConstants";
import { COMPOSER_TEXT_FIELD_SX } from "../../../components/MessageHistory/composerTextFieldStyles";
import {
  createVideoRoom,
  getVideoRoomByTeamSpace,
} from "../../../api/client";
import { invalidateActiveVideoRooms } from "../../../hooks/useActiveVideoRooms";
import { useQueryClient } from "react-query";
import { readTabUserData } from "../../../utils/tabAuthStorage";
import { openAgentVideoJoinUrl } from "../../../utils/activeVideoUi";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { useSocketContext } from "../../../context/SocketContext";
import {
  applyPickedMention,
  buildComposerMentionsPayload,
  buildMentionCatalog,
  buildTeamMentionOptions,
  emptyTeamMentions,
  findActiveMentionAt,
  getSpaceMemberUserIds,
  isEntityDiscussionSpace,
  shouldRestrictMentionsToMembers,
} from "../utils/teamMentions";
import TeamComposerReplyBar from "./TeamComposerReplyBar";
import {
  TEAM_MOBILE_MEDIA_QUERY,
  TEAM_MOBILE_COMPOSER_ROW_HEIGHT_PX,
} from "../utils/teamUiUtils";
import { useTeamTypingNotifier } from "../../../hooks/useTeamTypingNotifier";
import { resetMobileViewportAfterKeyboard } from "../../../hooks/useMobileViewportReset";
import {
  clearTeamMessageDraft,
  loadTeamMessageDraft,
  saveTeamMessageDraft,
} from "../utils/teamMessageDrafts";

const MENTION_OVERLAY_Z_INDEX = 10000;

function buildReplyPayload(replyTo) {
  if (replyTo?.id == null) return {};
  return {
    replyToId: Number(replyTo.id),
    replyPreview: {
      id: replyTo.id,
      authorName: replyTo.authorName,
      authorType: replyTo.authorType,
      content: replyTo.content,
      messageType: replyTo.messageType || "text",
      imageUrl: replyTo.imageUrl,
      videoUrl: replyTo.videoUrl,
      audioUrl: replyTo.audioUrl,
      fileUrl: replyTo.fileUrl,
      fileName: replyTo.fileName,
    },
  };
}

const TeamMessageInput = forwardRef(function TeamMessageInput(
  {
    onSend,
    isSending,
    leadId,
    currentUserId = null,
    members = null,
    space = null,
    overlayZIndex = MENTION_OVERLAY_Z_INDEX,
    replyTo = null,
    onClearReply,
    editingMessage = null,
    onCancelEdit,
    embedded = false,
    teamCompany = null,
  },
  ref
) {
  const { t } = useTranslation("team");
  const { t: tChat } = useTranslation("chat");
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { socket } = useSocketContext();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [expandedEditorOpen, setExpandedEditorOpen] = useState(false);
  const [pickedMentions, setPickedMentions] = useState(emptyTeamMentions);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [mentionOverlayPosition, setMentionOverlayPosition] = useState(null);
  const [textCursor, setTextCursor] = useState(0);
  const [videoSending, setVideoSending] = useState(false);
  const [joinVideoSending, setJoinVideoSending] = useState(false);
  const [activeVideoRoom, setActiveVideoRoom] = useState(null);
  const [videoGuide, setVideoGuide] = useState(null);
  const [startVideoDialogOpen, setStartVideoDialogOpen] = useState(false);
  const [videoReuseSnackbarOpen, setVideoReuseSnackbarOpen] = useState(false);
  const [teamVideoStartedSnackbarOpen, setTeamVideoStartedSnackbarOpen] = useState(false);

  const fileInputRef = useRef(null);
  const mentionAnchorRef = useRef(null);
  const textInputRef = useRef(null);
  const textRef = useRef("");
  const spaceIdRef = useRef(space?.id ?? null);
  const isEditModeRef = useRef(false);
  const draftContextRef = useRef({ userId: currentUserId, teamCompany });

  const restrictMentionsToMembers = shouldRestrictMentionsToMembers(space);
  const showMentionJoinStatus = isEntityDiscussionSpace(space);
  const memberUserIds = useMemo(() => getSpaceMemberUserIds(members), [members]);
  const isEditMode = Boolean(editingMessage?.id);

  const draftContext = useMemo(
    () => ({ userId: currentUserId, teamCompany }),
    [currentUserId, teamCompany]
  );

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  useEffect(() => {
    draftContextRef.current = draftContext;
  }, [draftContext]);

  useEffect(() => {
    const nextSpaceId = space?.id ?? null;
    const prevSpaceId = spaceIdRef.current;

    if (prevSpaceId && prevSpaceId !== nextSpaceId && !isEditModeRef.current) {
      saveTeamMessageDraft(prevSpaceId, textRef.current, draftContextRef.current);
    }

    if (nextSpaceId && !isEditMode) {
      const draft = loadTeamMessageDraft(nextSpaceId, draftContext);
      setText(draft);
      setTextCursor(draft.length);
      setPickedMentions(emptyTeamMentions());
    } else if (!nextSpaceId) {
      setText("");
      setTextCursor(0);
    }

    spaceIdRef.current = nextSpaceId;
  }, [space?.id, isEditMode, draftContext]);

  useEffect(() => {
    if (!space?.id || isEditMode) return undefined;
    const timer = setTimeout(() => {
      if (spaceIdRef.current === space.id && !isEditModeRef.current) {
        saveTeamMessageDraft(space.id, textRef.current, draftContextRef.current);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [space?.id, text, isEditMode]);

  useEffect(
    () => () => {
      const sid = spaceIdRef.current;
      if (sid && !isEditModeRef.current) {
        saveTeamMessageDraft(sid, textRef.current, draftContextRef.current);
      }
    },
    []
  );

  const { notifyActivity, stopTyping } = useTeamTypingNotifier(space?.id, {
    company: teamCompany,
    enabled: Boolean(space?.id) && !isEditMode,
  });

  const handleComposerTextChange = useCallback(
    (nextText) => {
      setText(nextText);
      const plain = composerPlainText(nextText, { trim: true });
      if (plain) {
        notifyActivity();
      } else {
        stopTyping();
      }
    },
    [notifyActivity, stopTyping]
  );

  const {
    audioURL,
    isRecording,
    recordingDuration,
    startRecording,
    stopRecording,
    discardRecording,
    handleRemoveAudio,
    clearAudio,
  } = useComposerAudioRecording({
    onNotSupported: () => alert(t("audioNotSupported")),
    onPermissionDenied: () => alert(t("audioPermissionDenied")),
  });

  const { data: usersData } = useUsers(1, 200, "", restrictMentionsToMembers ? false : "todos");
  const { data: agentsData } = useAgents(1, 100, "");

  const globalUsers = restrictMentionsToMembers ? [] : usersData?.users || [];
  const globalAgents = restrictMentionsToMembers ? [] : agentsData?.agents || [];

  const mentionCatalog = useMemo(
    () =>
      buildMentionCatalog({
        members,
        globalUsers,
        globalAgents,
        currentUserId,
        restrictToMembers: restrictMentionsToMembers,
      }),
    [members, globalUsers, globalAgents, currentUserId, restrictMentionsToMembers]
  );

  const { users, agents, parseUsers, parseAgents } = mentionCatalog;

  const messagePlainText = composerPlainText(text, { trim: false });
  const activeMention = findActiveMentionAt(messagePlainText, textCursor);
  const lastAt = activeMention?.atIndex ?? -1;
  const mentionQuery = activeMention?.query ?? "";
  const mentionPickerActive = lastAt >= 0;

  const mentionOptions = useMemo(
    () =>
      buildTeamMentionOptions({
        users,
        agents,
        query: mentionQuery,
        memberUserIds,
        showMemberJoinStatus: showMentionJoinStatus,
        labels: {
          allUsers: t("mentionAllUsers"),
          human: t("peopleGroup"),
          chatbot: t("chatbotsGroup"),
          agent: t("agentsGroup"),
        },
      }),
    [users, agents, mentionQuery, memberUserIds, showMentionJoinStatus, t]
  );

  const busy = (isEditMode && isSending) || videoSending || joinVideoSending;
  const hasPayload =
    composerHasTextPayload(text) || (!isEditMode && (files.length > 0 || Boolean(audioURL)));
  const canSend = hasPayload && !busy && !isRecording;
  const showMicButton = !embedded && !isEditMode && !hasPayload && !isRecording;
  const showTextComposer = !isRecording && !audioURL;
  const messagePlaceholder = embedded
    ? t("sidebarMessagePlaceholderShort")
    : isEditMode
      ? t("editMessagePlaceholder")
      : isMobile
        ? t("messagePlaceholderMobile")
        : t("messagePlaceholder");

  const compactTextValue = isProbablyHtml(text)
    ? stripHtmlToPlain(text, { trim: false })
    : text;

  const clearComposer = ({ clearDraft = true, restoreDraftAfter = false } = {}) => {
    stopTyping();
    if (clearDraft && space?.id && !isEditMode) {
      clearTeamMessageDraft(space.id, draftContext);
    }
    setText("");
    setTextCursor(0);
    setFiles([]);
    setPickedMentions(emptyTeamMentions());
    clearAudio();
    onClearReply?.();
    onCancelEdit?.();

    if (restoreDraftAfter && space?.id) {
      const draft = loadTeamMessageDraft(space.id, draftContext);
      setText(draft);
      setTextCursor(draft.length);
    }
  };

  const buildMentionsPayload = useCallback(
    () => buildComposerMentionsPayload(text, pickedMentions, { users: parseUsers, agents: parseAgents }),
    [text, parseUsers, parseAgents, pickedMentions]
  );

  const handleSelectMention = useCallback(
    (option) => {
      if (lastAt < 0 || !option) return;
      const label = option.label || "";
      const before = messagePlainText.slice(0, lastAt + 1);
      const after = messagePlainText.slice(textCursor);
      const nextText = `${before}${label} ${after}`;
      const nextCursor = before.length + label.length + 1;
      handleComposerTextChange(nextText);
      setTextCursor(nextCursor);
      setPickedMentions((prev) => applyPickedMention(prev, option));
      queueMicrotask(() => {
        const el = textInputRef.current;
        if (el) {
          el.focus();
          try {
            el.setSelectionRange(nextCursor, nextCursor);
          } catch {
            /* ignore */
          }
        }
      });
    },
    [lastAt, messagePlainText, textCursor, handleComposerTextChange]
  );

  useEffect(() => {
    if (editingMessage?.content != null) {
      setText(String(editingMessage.content));
      setTextCursor(String(editingMessage.content).length);
      setPickedMentions(emptyTeamMentions());
    }
  }, [editingMessage?.id, editingMessage?.content]);

  const syncTextCursor = useCallback((el) => {
    if (!el || typeof el.selectionStart !== "number") return;
    setTextCursor(el.selectionStart);
  }, []);

  useEffect(() => {
    setMentionHighlightIndex(0);
  }, [mentionOptions.length, mentionQuery]);

  const mentionPickerOpen = mentionPickerActive && mentionOptions.length > 0;

  useLayoutEffect(() => {
    if (mentionPickerOpen && mentionAnchorRef.current) {
      const rect = mentionAnchorRef.current.getBoundingClientRect();
      const gap = 8;
      setMentionOverlayPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: Math.max(rect.width, 300),
      });
    } else {
      setMentionOverlayPosition(null);
    }
  }, [mentionPickerOpen, mentionOptions.length, text]);

  useEffect(() => {
    if (!mentionPickerOpen) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (lastAt >= 0) setText(messagePlainText.slice(0, lastAt));
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        const opt = mentionOptions[mentionHighlightIndex];
        if (opt) handleSelectMention(opt);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setMentionHighlightIndex((i) => (i + 1) % mentionOptions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setMentionHighlightIndex(
          (i) => (i - 1 + mentionOptions.length) % mentionOptions.length
        );
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [
    mentionPickerOpen,
    mentionOptions,
    mentionHighlightIndex,
    lastAt,
    messagePlainText,
    handleSelectMention,
  ]);

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveAudioAndFiles = () => {
    handleRemoveAudio();
    setFiles([]);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const classifyVideoError = useCallback(
    (e) => {
      const code = e?.response?.data?.code || e?.code || e?.userFacing?.code;
      const raw = getApiErrorMessage(
        e,
        tChat("video.errorGeneric", "Não foi possível iniciar a chamada de vídeo.")
      );
      if (
        code === "video.channel_missing" ||
        /canal de Chamadas|Video Calls channel|canal de Llamadas|canale Chiamate/i.test(raw)
      ) {
        return {
          kind: "channel_missing",
          title: tChat("video.channelMissingTitle", "Canal de vídeo necessário"),
          message: tChat(
            "video.channelMissing",
            "Crie um canal de Chamadas de Vídeo em Configurações → Canais."
          ),
        };
      }
      if (
        code === "video.schema_missing" ||
        /twilio_video_rooms|doesn't exist|ER_NO_SUCH_TABLE|ainda está sendo preparado|still being prepared/i.test(
          raw
        )
      ) {
        return {
          kind: "schema_missing",
          title: tChat("video.schemaPreparingTitle", "Preparando vídeo"),
          message: tChat(
            "video.schemaPreparing",
            "O recurso de vídeo ainda está sendo preparado neste workspace. Tente novamente em alguns segundos."
          ),
        };
      }
      if (
        code === "video.session_inactive" ||
        code === "errors.auth.user_inactive" ||
        /usuário inválido ou inativo/i.test(raw)
      ) {
        return {
          kind: "session",
          title: tChat("video.sessionInactiveTitle", "Sessão inválida neste workspace"),
          message: tChat(
            "video.sessionInactive",
            "Sua sessão não é válida neste workspace. Confirme que está no workspace certo, entre novamente se preciso e abra a chamada pelo chat da equipe."
          ),
        };
      }
      if (
        code === "video.session_required" ||
        code === "video.session_revoked" ||
        code === "video.session_invalid" ||
        code === "video.session_workspace_mismatch" ||
        code === "errors.auth.session_revoked" ||
        code === "errors.auth.token_missing" ||
        code === "errors.auth.token_invalid"
      ) {
        return {
          kind: "session",
          title: tChat("video.sessionInvalidTitle", "Não foi possível validar sua sessão"),
          message: tChat(
            "video.sessionInvalid",
            "Entre novamente no XPro no workspace correto e abra a chamada pelo chat da equipe."
          ),
        };
      }
      return {
        kind: "generic",
        title: tChat("video.errorTitle", "Chamada de vídeo"),
        message: raw,
      };
    },
    [tChat]
  );

  const openAgentVideoUrl = useCallback(
    (joinUrl) => {
      if (!joinUrl) return;
      const userData = readTabUserData() || {};
      const agentName = String(userData?.nome || userData?.name || "").trim().slice(0, 80);
      openAgentVideoJoinUrl(joinUrl, {
        context: "team",
        name: agentName,
        onBlocked: () => {
          setVideoGuide({
            kind: "popup_blocked",
            title: tChat("video.popupBlockedTitle", "Pop-up bloqueado"),
            message: tChat(
              "video.popupBlocked",
              "O navegador bloqueou a abertura da chamada. Permita pop-ups para este site e toque em Entrar novamente."
            ),
          });
        },
      });
    },
    [tChat]
  );

  useEffect(() => {
    const spaceId = Number(space?.id);
    if (!Number.isFinite(spaceId) || spaceId <= 0) {
      setActiveVideoRoom(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const room = await getVideoRoomByTeamSpace(spaceId);
        if (!cancelled) setActiveVideoRoom(room || null);
      } catch {
        if (!cancelled) setActiveVideoRoom(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [space?.id]);

  useEffect(() => {
    if (!socket) return undefined;
    const spaceId = Number(space?.id);
    const onVideoRoom = (raw) => {
      let payload = raw;
      if (typeof raw === "string") {
        try {
          payload = JSON.parse(raw);
        } catch {
          return;
        }
      }
      if (!payload || typeof payload !== "object") return;
      const action = String(payload.action || "");
      const teamSpaceId = Number(payload.teamSpaceId);
      if (
        action === "started" &&
        Number.isFinite(spaceId) &&
        spaceId > 0 &&
        teamSpaceId === spaceId
      ) {
        setTeamVideoStartedSnackbarOpen(true);
        invalidateActiveVideoRooms(queryClient);
        (async () => {
          try {
            const room = await getVideoRoomByTeamSpace(spaceId);
            setActiveVideoRoom(room || null);
          } catch {
            /* ignore */
          }
        })();
      }
    };
    socket.on("video_room", onVideoRoom);
    return () => {
      socket.off("video_room", onVideoRoom);
    };
  }, [socket, space?.id, queryClient]);

  const handleJoinActiveVideo = useCallback(async () => {
    if (joinVideoSending) return;
    const spaceId = Number(space?.id);
    if (!Number.isFinite(spaceId) || spaceId <= 0) return;
    setJoinVideoSending(true);
    try {
      let room = activeVideoRoom;
      if (!room?.joinUrl && !room?.agentJoinUrl) {
        room = await getVideoRoomByTeamSpace(spaceId);
        setActiveVideoRoom(room || null);
      }
      const joinUrl = room?.agentJoinUrl || room?.joinUrl;
      if (!joinUrl) throw new Error("missing_join_url");
      openAgentVideoUrl(joinUrl);
    } catch (e) {
      setVideoGuide(classifyVideoError(e));
      setActiveVideoRoom(null);
    } finally {
      setJoinVideoSending(false);
    }
  }, [
    joinVideoSending,
    space?.id,
    activeVideoRoom,
    openAgentVideoUrl,
    classifyVideoError,
  ]);

  const handleStartXproVideo = useCallback(async (opts = {}) => {
    if (videoSending || isEditMode || !onSend) return;
    const spaceId = Number(space?.id);
    if (!Number.isFinite(spaceId) || spaceId <= 0) return;
    const requireCamera = Boolean(opts.requireCamera);
    const isPrivate = Boolean(opts.isPrivate);
    const recordingVisibility =
      opts.recordingVisibility === "creator" ? "creator" : "space";
    setStartVideoDialogOpen(false);
    setVideoSending(true);
    try {
      const result = await createVideoRoom({
        teamSpaceId: spaceId,
        requireCamera,
        isPrivate,
        ...(opts.recordingEnabled != null
          ? { recordingEnabled: Boolean(opts.recordingEnabled) }
          : {}),
        ...(opts.recordingEnabled
          ? { recordingVisibility }
          : {}),
      });
      const joinUrl = result?.joinUrl || result?.agentJoinUrl;
      if (!joinUrl) throw new Error("missing_join_url");

      setActiveVideoRoom({ ...result, canJoin: result?.canJoin !== false });
      invalidateActiveVideoRooms(queryClient);
      if (result?.reused) {
        setVideoReuseSnackbarOpen(true);
      }

      const roomRequireCamera = Boolean(result?.requireCamera ?? requireCamera);
      const inviteBody =
        result?.inviteBody ||
        (roomRequireCamera
          ? tChat(
              "video.inviteBodyTeamCameraRequired",
              "Sua equipe iniciou uma chamada com câmera obrigatória. Entre com a câmera ligada — sem câmera neste dispositivo, não é possível participar."
            )
          : tChat(
              "video.inviteBodyTeam",
              "Sua equipe iniciou uma chamada de vídeo. Entre para participar."
            ));
      const inviteCta =
        result?.inviteCta ||
        (roomRequireCamera
          ? tChat("video.inviteCtaTeamCameraRequired", "Entrar com a câmera")
          : tChat("video.inviteCtaTeam", "Entrar na chamada da equipe"));
      let joinUrlWithContext = joinUrl;
      let agentJoinUrl = result?.agentJoinUrl || joinUrl;
      try {
        const guest = new URL(joinUrlWithContext, window.location.origin);
        guest.searchParams.set("context", "team");
        joinUrlWithContext = guest.toString();
        const u = new URL(agentJoinUrl, window.location.origin);
        u.searchParams.set("role", "agent");
        u.searchParams.set("context", "team");
        agentJoinUrl = u.toString();
      } catch {
        /* keep */
      }

      if (!result?.reused) {
        await onSend({
          content: `${inviteBody}\n${joinUrlWithContext}`,
          mentions: emptyTeamMentions(),
          ...(leadId ? { leadId } : {}),
          metadata: {
            xpro_team_video_invite: true,
            guestJoinUrl: joinUrlWithContext,
            agentJoinUrl,
            inviteBody,
            inviteCta,
            buttonMessage: `${inviteCta}|${joinUrlWithContext}`,
            interactive: { ctaUrl: joinUrlWithContext, ctaDisplayText: inviteCta },
            roomId: result?.id ?? null,
            callContext: "team",
            requireCamera: roomRequireCamera,
            isPrivate,
            ...(opts.recordingEnabled != null
              ? { recordingEnabled: Boolean(opts.recordingEnabled) }
              : {}),
          },
        });
      }

      openAgentVideoUrl(agentJoinUrl);
    } catch (e) {
      setVideoGuide(classifyVideoError(e));
    } finally {
      setVideoSending(false);
    }
  }, [
    videoSending,
    isEditMode,
    onSend,
    space?.id,
    leadId,
    classifyVideoError,
    openAgentVideoUrl,
    tChat,
    queryClient,
  ]);

  const handleFileUpload = useCallback((fileOrFiles) => {
    if (!fileOrFiles || isRecording) return;
    const toAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    setFiles((prev) => [...prev, ...toAdd]);
  }, [isRecording]);

  useImperativeHandle(ref, () => ({ appendFiles: handleFileUpload }), [handleFileUpload]);

  const handleFileUploadInput = (e) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    if (selected.length) {
      handleFileUpload(selected);
    }
    e.target.value = "";
  };

  const handleEmojiPickerOpen = (event) => setEmojiAnchorEl(event.currentTarget);
  const handleEmojiPickerClose = () => setEmojiAnchorEl(null);
  const handleSelectEmoji = (emoji) => {
    handleComposerTextChange(`${typeof text === "string" ? text : ""}${emoji}`);
  };

  const submitComposer = () => {
    stopTyping();
    const caption = composerPlainText(text, { trim: true });
    const mentions = caption ? buildMentionsPayload() : emptyTeamMentions();
    const leadPayload = leadId ? { leadId } : {};
    const replyPayload = buildReplyPayload(replyTo);

    if (isEditMode) {
      if (!caption) return;
      onSend({
        editMessageId: editingMessage.id,
        content: caption,
        mentions,
      })
        .then(() => {
          clearComposer();
          refocusComposerAfterSend();
        })
        .catch((err) => console.error("[TeamMessageInput] edição:", err));
      return;
    }

    const sendItems = [];

    if (caption) {
      sendItems.push({
        content: caption,
        mentions,
        ...replyPayload,
        ...leadPayload,
      });
    }

    for (const file of files) {
      sendItems.push({
        _file: file,
        mentions: emptyTeamMentions(),
        ...replyPayload,
        ...leadPayload,
      });
    }

    if (audioURL) {
      sendItems.push({
        _audioURL: audioURL,
        mentions: emptyTeamMentions(),
        ...replyPayload,
        ...leadPayload,
      });
    }

    if (!sendItems.length) return;

    clearComposer();
    refocusComposerAfterSend();

    for (const payload of sendItems) {
      onSend(payload).catch((err) => console.error("[TeamMessageInput] envio:", err));
    }
  };

  const handleSend = () => {
    if (!canSend) return;
    if (mentionPickerOpen) return;
    submitComposer();
  };

  const handleExpandedSend = () => {
    const canExpandedSend =
      composerHasTextPayload(text) ||
      composerHasSendPayload("", files[0], files, audioURL);
    if (!canExpandedSend || busy || isRecording) return;
    submitComposer();
    setExpandedEditorOpen(false);
  };

  const handleTextKeyDown = (e) => {
    if (mentionPickerOpen) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) handleSend();
    }
  };

  const embeddedRowHeight = 44;
  const composerRowHeight = embedded
    ? embeddedRowHeight
    : isMobile
      ? TEAM_MOBILE_COMPOSER_ROW_HEIGHT_PX
      : COMPOSER_ROW_HEIGHT_PX;

  const composerTextFieldSx = useMemo(() => {
    const mobileTextareaBase = {
      boxSizing: "border-box",
      fontSize: "16px",
      lineHeight: "20px",
      padding: "14px 10px",
      resize: "none",
      overflowX: "hidden",
      WebkitTextSizeAdjust: "100%",
      touchAction: "manipulation",
    };

    if (embedded) {
      const embeddedPadY = Math.max(0, Math.floor((composerRowHeight - 20) / 2));
      return {
        ...COMPOSER_TEXT_FIELD_SX,
        "& .MuiInputBase-root": {
          ...COMPOSER_TEXT_FIELD_SX["& .MuiInputBase-root"],
          minHeight: `${composerRowHeight}px`,
          alignItems: "center",
          overflow: "hidden",
          "&:has(textarea:placeholder-shown)": {
            minHeight: `${composerRowHeight}px`,
            maxHeight: `${composerRowHeight}px`,
          },
        },
        "& textarea, & .MuiInputBase-inputMultiline": {
          ...COMPOSER_TEXT_FIELD_SX["& textarea, & .MuiInputBase-inputMultiline"],
          ...mobileTextareaBase,
          padding: `${embeddedPadY}px 8px`,
          minHeight: `${composerRowHeight}px`,
          "&:placeholder-shown": {
            height: "auto !important",
            minHeight: `${composerRowHeight}px !important`,
            maxHeight: `${composerRowHeight}px !important`,
            lineHeight: "20px !important",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          },
          "&:not(:placeholder-shown)": {
            minHeight: "unset",
            maxHeight: "none",
            height: "auto !important",
          },
        },
      };
    }

    if (!isMobile) return COMPOSER_TEXT_FIELD_SX;

    return {
      flex: 1,
      minWidth: 0,
      "& .MuiInputBase-root": {
        minHeight: `${composerRowHeight}px`,
        boxSizing: "border-box",
        borderRadius: "0 !important",
        alignItems: "center",
        overflow: "hidden",
        "&:has(textarea:placeholder-shown)": {
          minHeight: `${composerRowHeight}px`,
          maxHeight: `${composerRowHeight}px`,
        },
      },
      "& .MuiInputAdornment-root": {
        alignSelf: "center",
        marginTop: "0 !important",
        marginBottom: "0 !important",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: "0 !important",
        overflow: "hidden",
        "& fieldset": { border: "none" },
        "&:hover fieldset": { border: "none" },
        "&.Mui-focused fieldset": { border: "none" },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none !important",
      },
      "& textarea, & .MuiInputBase-inputMultiline": {
        ...mobileTextareaBase,
        overflowY: "auto",
        minHeight: `${composerRowHeight}px`,
        "&:placeholder-shown": {
          height: "auto !important",
          minHeight: `${composerRowHeight}px !important`,
          maxHeight: `${composerRowHeight}px !important`,
          lineHeight: "20px !important",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        },
        "&:not(:placeholder-shown)": {
          minHeight: "unset",
          maxHeight: "none",
          height: "auto !important",
        },
      },
    };
  }, [embedded, isMobile, composerRowHeight]);

  const handleComposerBlur = useCallback(() => {
    if (!isMobile) return;
    window.setTimeout(resetMobileViewportAfterKeyboard, 80);
  }, [isMobile]);

  const refocusComposerAfterSend = useCallback(() => {
    queueMicrotask(() => {
      const el = textInputRef.current;
      if (!el || busy || isRecording) return;
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    });
  }, [busy, isRecording]);
  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={embedded ? 2 : 4}
      placeholder={messagePlaceholder}
      value={compactTextValue}
      onChange={(e) => {
        handleComposerTextChange(e.target.value);
        syncTextCursor(e.target);
      }}
      onSelect={(e) => syncTextCursor(e.target)}
      onClick={(e) => syncTextCursor(e.target)}
      onKeyUp={(e) => syncTextCursor(e.target)}
      onBlur={handleComposerBlur}
      disabled={busy}
      variant="outlined"
      inputRef={textInputRef}
      InputProps={{
        style: {
          borderRadius: 0,
          fontSize: isMobile ? "16px" : embedded ? "0.875rem" : "0.9375rem",
        },
        startAdornment: embedded ? undefined : (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleEmojiPickerOpen}
              disabled={busy}
              edge="start"
              aria-label={tChat("input.emojiAria")}
              sx={{ color: "text.secondary" }}
            >
              <InsertEmoticonIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: !isMobile && !embedded ? (
          <InputAdornment
            position="end"
            sx={{ mr: 0.25, display: "flex", alignItems: "center" }}
          >
            <Tooltip title={tChat("compose.expandInternalEditor")}>
              <IconButton
                size="small"
                onClick={() => setExpandedEditorOpen(true)}
                disabled={busy}
                aria-label={tChat("compose.expandInternalEditor")}
                sx={{ color: "text.secondary" }}
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
      sx={composerTextFieldSx}
      onKeyDown={handleTextKeyDown}
    />
  );

  const showSendSpinner = isEditMode && isSending;

  const sendActionButton = (
    <Tooltip
      title={
        isRecording
          ? tChat("input.sendAfterStopTooltip")
          : showMicButton
            ? tChat("input.recordVoiceAria")
            : tChat("input.sendAria")
      }
      enterDelay={400}
      disableHoverListener={!isRecording && !showMicButton && !canSend}
    >
      <Box component="span" sx={{ display: "inline-flex", alignSelf: "center", flexShrink: 0 }}>
        <IconButton
          onMouseDown={(e) => e.preventDefault()}
          onClick={showMicButton ? startRecording : handleSend}
          disabled={busy || isRecording || (!showMicButton && !canSend)}
          aria-label={showMicButton ? tChat("input.recordVoiceAria") : tChat("input.sendAria")}
          sx={
            embedded
              ? {
                  ...composerPrimaryActionButtonSx(),
                  width: 36,
                  height: 36,
                  m: 0.5,
                }
              : composerPrimaryActionButtonSx()
          }
        >
          {showSendSpinner ? (
            <CircularProgress size={embedded ? 18 : 22} color="inherit" />
          ) : showMicButton ? (
            <MicIcon fontSize={embedded ? "small" : "medium"} />
          ) : (
            <SendIcon fontSize={embedded ? "small" : "medium"} />
          )}
        </IconButton>
      </Box>
    </Tooltip>
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={handleRemoveAudioAndFiles} />
  ) : (
    textField
  );

  const composerShellBg =
    theme.palette.mode === "dark" ? "background.default" : alpha(theme.palette.grey[50], 0.8);

  return (
    <Box
      sx={{
        flexShrink: 0,
        position: "relative",
        zIndex: 2,
        px: embedded ? 0.75 : { xs: 1, sm: 2 },
        py: embedded ? 0.5 : { xs: 0.5, sm: 1.5 },
        pb: embedded ? 0.5 : { xs: 0, sm: 1.5 },
        borderTop: 1,
        borderColor: alpha(theme.palette.divider, 0.35),
        bgcolor: "background.paper",
        maxWidth: "100%",
        overflow: "visible",
      }}
    >
      <TeamComposerReplyBar replyTo={replyTo} onClear={onClearReply} />
      {isEditMode ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.75,
            px: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("editingMessage")}
          </Typography>
          <Button
            size="small"
            onClick={() => clearComposer({ clearDraft: false, restoreDraftAfter: true })}
            disabled={busy}
          >
            {t("cancelEdit")}
          </Button>
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: embedded ? 0 : isMobile ? 0.5 : 0.75,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          ref={mentionAnchorRef}
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "stretch",
            minHeight: composerRowHeight,
            border: 1,
            borderColor: (th) => alpha(th.palette.divider, th.palette.mode === "dark" ? 0.6 : 1),
            borderRadius: embedded ? 1.75 : 2,
            overflow: "hidden",
            bgcolor: composerShellBg,
            transition: "border-color 0.15s, box-shadow 0.15s",
            "&:focus-within": {
              borderColor: "primary.main",
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
            onChange={handleFileUploadInput}
          />

          {showTextComposer ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                alignSelf: "stretch",
                px: embedded ? 0 : 0.25,
                borderRight: 1,
                borderColor: "divider",
              }}
            >
              <ComposeAttachMenu
                size={embedded ? "small" : "medium"}
                openFilePicker={openFilePicker}
                fileSendDisabled={busy || isEditMode}
                showStartVideo={!isEditMode}
                onStartVideo={() => setStartVideoDialogOpen(true)}
                videoSending={videoSending}
                videoSendDisabled={busy || isEditMode || !space?.id}
                videoMenuLabel={tChat("video.inviteTeamXpro", "Chamada de vídeo com a equipe")}
                showJoinVideo={
                  !isEditMode &&
                  activeVideoRoom?.canJoin !== false &&
                  Boolean(activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl)
                }
                onJoinVideo={handleJoinActiveVideo}
                joinVideoSending={joinVideoSending}
              />
              {embedded ? (
                <Tooltip title={tChat("input.emojiAria")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleEmojiPickerOpen}
                      disabled={busy}
                      aria-label={tChat("input.emojiAria")}
                      sx={{ color: "text.secondary", p: 0.65 }}
                    >
                      <InsertEmoticonIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          ) : null}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: `${composerRowHeight}px`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {textOrVoiceSlot}
          </Box>

          {embedded ? sendActionButton : null}
        </Box>

        {!embedded ? sendActionButton : null}
      </Box>

      {files.length > 0 ? (
        <Box
          sx={{
            width: "100%",
            mt: 0.75,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {files.map((file, index) => (
            <FilePreview key={`${file.name}-${index}`} file={file} onRemove={() => handleRemoveFile(index)} />
          ))}
        </Box>
      ) : null}

      {mentionOverlayPosition && mentionPickerOpen
        ? createPortal(
            <Box
              role="listbox"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                position: "fixed",
                bottom: mentionOverlayPosition.bottom,
                left: mentionOverlayPosition.left,
                width: mentionOverlayPosition.width,
                maxHeight: "min(320px, 50vh)",
                overflow: "auto",
                bgcolor: "background.paper",
                boxShadow: theme.shadows[8],
                borderRadius: 1.5,
                zIndex: overlayZIndex,
                border: 1,
                borderColor: "divider",
              }}
            >
              {showMentionJoinStatus ? (
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: alpha(theme.palette.action.hover, 0.04),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {t("mentionJoinStatusHint")}
                  </Typography>
                </Box>
              ) : null}
              {mentionOptions.map((opt, idx) => (
                <Box
                  key={`${opt.type}-${opt.id}-${idx}`}
                  component="div"
                  role="option"
                  aria-selected={idx === mentionHighlightIndex}
                  onClick={() => handleSelectMention(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor:
                      idx === mentionHighlightIndex ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "baseline", gap: 0.75 }}>
                    <Typography variant="body2" fontWeight={opt.type === "all" ? 700 : 500} noWrap>
                      @{opt.label}
                    </Typography>
                    {opt.kind ? (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {opt.kind}
                      </Typography>
                    ) : null}
                  </Box>
                  {opt.memberStatus === "member" ? (
                    <Chip
                      size="small"
                      label={t("mentionAlreadyMember")}
                      variant="outlined"
                      sx={{ height: 22, flexShrink: 0, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
                    />
                  ) : opt.memberStatus === "willJoin" ? (
                    <Chip
                      size="small"
                      label={t("mentionWillJoin")}
                      color="primary"
                      variant="outlined"
                      sx={{ height: 22, flexShrink: 0, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
                    />
                  ) : null}
                </Box>
              ))}
            </Box>,
            document.body
          )
        : null}

      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiPickerClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { maxHeight: "min(400px, 70vh)", overflow: "auto" } },
        }}
      >
        <EmojiPicker onSelectEmoji={handleSelectEmoji} />
      </Popover>

      <InternalNoteExpandedEditor
        open={expandedEditorOpen}
        onClose={() => setExpandedEditorOpen(false)}
        message={text}
        setMessage={handleComposerTextChange}
        onSend={handleExpandedSend}
        openFilePicker={openFilePicker}
        files={files}
        onRemoveFile={handleRemoveFile}
        audioURL={audioURL}
        title={t("expandedEditorTitle")}
        hint={t("expandedEditorHint")}
        sendLabel={t("expandedEditorSend")}
        cancelLabel={t("expandedEditorCancel")}
        closeAriaLabel={t("expandedEditorClose")}
      />

      <Dialog
        open={Boolean(videoGuide)}
        onClose={() => setVideoGuide(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{videoGuide?.title || tChat("video.errorTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {videoGuide?.message || tChat("video.errorGeneric")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVideoGuide(null)}>
            {tChat("video.errorClose", "Fechar")}
          </Button>
        </DialogActions>
      </Dialog>
      <StartVideoCallDialog
        open={startVideoDialogOpen}
        busy={videoSending}
        context="team"
        defaultPrivate={space?.type === "dm"}
        isDm={space?.type === "dm"}
        activeRoomExists={Boolean(
          activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl || activeVideoRoom?.id
        )}
        onClose={() => setStartVideoDialogOpen(false)}
        onConfirm={({ requireCamera, isPrivate, recordingEnabled, recordingVisibility }) =>
          handleStartXproVideo({
            requireCamera,
            isPrivate,
            recordingEnabled,
            recordingVisibility,
          })
        }
      />
      <Snackbar
        open={videoReuseSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setVideoReuseSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          onClose={() => setVideoReuseSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {tChat(
            "video.reusedRoomSnackbar",
            "Já existe uma chamada ativa — entrando na mesma sala"
          )}
        </Alert>
      </Snackbar>
      <Snackbar
        open={teamVideoStartedSnackbarOpen}
        autoHideDuration={5000}
        onClose={() => setTeamVideoStartedSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setTeamVideoStartedSnackbarOpen(false)}
          action={
            activeVideoRoom?.canJoin !== false &&
            (activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl) ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setTeamVideoStartedSnackbarOpen(false);
                  handleJoinActiveVideo();
                }}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {t("activeVideo.join")}
              </Button>
            ) : null
          }
          sx={{ width: "100%" }}
        >
          {t("activeVideo.startedSnackbar", "Chamada de vídeo iniciada neste espaço")}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default TeamMessageInput;
